import { ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Free Sample controller.
 *
 * Keeps at most ONE free trial-piece sample in the cart, sized to match the
 * qualifying product the customer is buying:
 *  - added when the cart has a qualifying (non-trial, non-excluded) item whose
 *    size maps to a trial size, AND the excluded product is not in the cart;
 *  - the trial SIZE follows the most-recently-added qualifying item's size
 *    (see SIZE_MAP); an unmapped/one-size item yields no sample;
 *  - removed when not eligible.
 *
 * The sample is made ₹0 by setting the trial product's price to ₹0 in Shopify
 * (no discount needed); this module only manages presence / size / quantity and
 * refreshes the cart UI.
 *
 * Design notes:
 *  - Single module-level singleton (guarded by window.__freeSampleInit). Config
 *    is read live from the <free-sample-controller> element each run, so morphing
 *    the header/drawer can neither re-bind listeners nor loop.
 *  - Refreshes via the Section Rendering API + morphSection() WITHOUT dispatching
 *    `cart:update` (which would pop the drawer open). Only writes when desired
 *    state differs from actual, which prevents an add/remove loop.
 */

const RECONCILE_DEBOUNCE_MS = 150;

/**
 * Customer size (normalised, uppercase, spaces removed) -> trial size title.
 * Sizes not present here (XS, 2XS, 3XL, 4XL, unknown) yield NO sample.
 */
const SIZE_MAP = {
  XS: 'S-M',
  S: 'S-M',
  M: 'M-L',
  L: 'L-XL',
  XL: 'XL-2XL',
  '2XL': 'XL-2XL',
  // Range-sized products map to the closest trial range.
  'S-M': 'S-M',
  'M-L': 'M-L',
  'L-XL': 'L-XL',
  'XL-XXL': 'XL-2XL',
  'XL-2XL': 'XL-2XL',
};

let busy = false;
/** @type {number | undefined} */
let debounceTimer;

/**
 * Normalises a size token: uppercase, strip spaces, and fold "XX" spellings
 * into "2X" forms (XXL->2XL, XXS->2XS, XXXL->3XL, XXXXL->4XL).
 * @param {string} raw
 * @returns {string}
 */
function normalizeSize(raw) {
  let s = String(raw || '').toUpperCase().replace(/\s+/g, '');
  s = s.replace('XXXXL', '4XL').replace('XXXL', '3XL').replace('XXL', '2XL').replace('XXS', '2XS');
  return s;
}

/**
 * Maps a customer size to a trial size title, or null if it should get no sample.
 * @param {string} raw
 * @returns {string | null}
 */
function trialSizeFor(raw) {
  const s = normalizeSize(raw);
  return SIZE_MAP[s] || null;
}

/**
 * Extracts the size option value from a cart line item.
 * @param {any} item
 * @returns {string | null}
 */
function itemSize(item) {
  const owv = item.options_with_values || [];
  const sizeOpt = owv.find((o) => /size/i.test(o.name));
  if (sizeOpt && sizeOpt.value) return sizeOpt.value;
  if (Array.isArray(item.variant_options) && item.variant_options.length === 1) {
    return item.variant_options[0];
  }
  return null;
}

/**
 * Reads live configuration from the controller element.
 * @returns {{ productId: number, excludedProductId: number, sizeVariantMap: Record<string, number> } | null}
 */
function getConfig() {
  const el = document.querySelector('free-sample-controller');
  if (!el || !(el instanceof HTMLElement)) return null;

  const productId = Number(el.dataset.productId) || 0;
  const excludedProductId = Number(el.dataset.excludedProductId) || 0;

  /** @type {Record<string, number>} */
  const sizeVariantMap = {};
  try {
    const parsed = JSON.parse(el.dataset.sizeVariantMap || '{}');
    for (const [key, value] of Object.entries(parsed)) {
      sizeVariantMap[normalizeSize(key)] = Number(value);
    }
  } catch (_) {
    // Malformed map — treat as empty (feature inert).
  }

  if (!productId || Object.keys(sizeVariantMap).length === 0) return null;

  return { productId, excludedProductId, sizeVariantMap };
}

/**
 * Collects the section ids of every cart-items-component on the page.
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * @param {Record<string, unknown>} body
 * @returns {Record<string, unknown>}
 */
function withSections(body) {
  const ids = getCartSectionIds();
  if (ids.length) {
    body.sections = ids.join(',');
    body.sections_url = window.location.pathname;
  }
  return body;
}

/**
 * Adds one sample unit (specific variant) to the cart.
 * @param {number} variantId
 * @returns {Promise<Record<string, any> | null>}
 */
async function addVariant(variantId) {
  const body = withSections({ id: variantId, quantity: 1, properties: { _free_sample: 'true' } });
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return response.ok ? response.json() : null;
}

/**
 * Sets a cart line quantity by line key (0 removes).
 * @param {string} key
 * @param {number} quantity
 * @returns {Promise<Record<string, any> | null>}
 */
async function changeLine(key, quantity) {
  const body = withSections({ id: key, quantity });
  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return response.ok ? response.json() : null;
}

/**
 * Re-promotes the cart drawer into the browser top layer if a morph knocked it
 * out. Morphing the header section (which contains the drawer <dialog>) can
 * re-insert the dialog and drop it from the top layer; it then renders at
 * z-index:auto, behind fixed third-party widgets. `showDialog()` won't fix this
 * because it early-returns while the dialog is already `open`. We detect the
 * "open but not modal" state and re-show it as a modal, with the animation
 * suppressed so the drawer doesn't visibly re-slide. Runs synchronously right
 * after a morph, so there is no paint in between and no flicker.
 */
function drawerDialog() {
  const dialog = document.querySelector('.cart-drawer__dialog');
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

function reassertDrawerTopLayer() {
  const dialog = drawerDialog();
  if (!dialog || !dialog.open || dialog.matches(':modal')) return;

  const previousAnimation = dialog.style.animation;
  dialog.style.animation = 'none';
  try {
    dialog.close();
    dialog.showModal();
  } catch (_) {
    // Ignore — dialog not attached / not showable.
  }
  // Force a reflow so `animation: none` applies to the re-show, then restore.
  void dialog.offsetWidth;
  dialog.style.animation = previousAnimation;
}

/**
 * Keeps the open drawer from being abruptly closed by a morph.
 *
 * A cart/discount interaction re-renders (morphs) the header section that
 * contains the drawer <dialog>. That morph can drop the dialog's `open`
 * attribute — closing the side cart with no animation — which is what happens
 * when a discount code is applied. This installs a short-lived observer while
 * the morph is in flight: if the drawer closes WITHOUT the theme's animated
 * close (which adds the `dialog-closing` class), we re-open it immediately, in a
 * microtask before paint, so there is no visible flicker. A genuine user close
 * (X button / Esc / click-outside) carries `dialog-closing` and is left alone.
 */
function guardDrawerOpen() {
  const dialog = drawerDialog();
  if (!dialog || !dialog.open) return; // only guard an already-open drawer

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    observer.disconnect();
    clearTimeout(timer);
  };

  const observer = new MutationObserver(() => {
    const d = drawerDialog();
    if (!d) return;
    if (!d.open) {
      if (d.classList.contains('dialog-closing')) {
        stop(); // genuine, animated close — leave it
        return;
      }
      // Abrupt morph close — restore it without replaying the open animation.
      const previousAnimation = d.style.animation;
      d.style.animation = 'none';
      try {
        d.showModal();
      } catch (_) { }
      void d.offsetWidth;
      d.style.animation = previousAnimation;
    } else if (!d.matches(':modal')) {
      reassertDrawerTopLayer();
    }
  });

  observer.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
  const timer = setTimeout(stop, 1500);
}

/**
 * Morphs each returned section into the page without dispatching cart:update.
 * @param {Record<string, string> | undefined} sections
 */
function refreshSections(sections) {
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    if (typeof html !== 'string') continue;
    try {
      morphSection(id, html);
    } catch (_) {
      // Section not on this page — ignore.
    }
  }
  // A morph of the header can drop the open drawer from the top layer — restore it.
  reassertDrawerTopLayer();
}

/**
 * Reads the true cart state and reconciles the sample presence / size / quantity.
 */
async function reconcile() {
  if (busy) {
    schedule();
    return;
  }

  const config = getConfig();
  if (!config) return;

  busy = true;
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    const items = Array.isArray(cart.items) ? cart.items : [];

    const { productId, excludedProductId, sizeVariantMap } = config;

    const sampleLine = items.find((item) => item.product_id === productId);
    const hasExcluded = excludedProductId
      ? items.some((item) => item.product_id === excludedProductId)
      : false;

    // Qualifying = any line that is neither the trial nor the excluded product.
    const qualifying = items.filter(
      (item) => item.product_id !== productId && item.product_id !== excludedProductId
    );

    // Driver = most recently added qualifying item. Shopify's cart orders the
    // most recently added line first (index 0), so that's the newest.
    const driver = qualifying.length ? qualifying[0] : null;

    // Resolve the desired trial variant (or null = no sample).
    let desiredVariantId = null;
    if (!hasExcluded && driver) {
      const trialTitle = trialSizeFor(itemSize(driver));
      if (trialTitle) {
        const mapped = sizeVariantMap[normalizeSize(trialTitle)];
        if (mapped) desiredVariantId = mapped;
      }
    }

    /** @type {Record<string, any> | null} */
    let result = null;

    if (!desiredVariantId) {
      if (sampleLine) result = await changeLine(sampleLine.key, 0);
    } else if (!sampleLine) {
      result = await addVariant(desiredVariantId);
    } else if (Number(sampleLine.variant_id) !== desiredVariantId) {
      // Size changed — swap the sample: remove the old line, add the new size.
      await changeLine(sampleLine.key, 0);
      result = await addVariant(desiredVariantId);
    } else if (sampleLine.quantity !== 1) {
      result = await changeLine(sampleLine.key, 1);
    }

    if (result) refreshSections(result.sections);
  } catch (error) {
    console.error('[free-sample]', error);
  } finally {
    busy = false;
  }
}

function schedule() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(reconcile, RECONCILE_DEBOUNCE_MS);
}

if (!window.__freeSampleInit) {
  window.__freeSampleInit = true;

  // A cart or discount interaction morphs the header and can either drop the open
  // drawer from the top layer or close it outright. Guard against both.
  const onCartInteraction = () => {
    guardDrawerOpen();
    requestAnimationFrame(reassertDrawerTopLayer);
    setTimeout(reassertDrawerTopLayer, 300);
  };

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.source === 'free-sample') return;
    schedule();
    onCartInteraction();
  });

  // Applying/removing a discount code re-renders the header (discount:update) and
  // can abruptly close the drawer — keep it open.
  document.addEventListener(ThemeEvents.discountUpdate, onCartInteraction);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
}
